import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";

import { Breadcrumbs, type Crumb } from "../ui/breadcrumbs";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

type MenuElement = "category" | "item" | "modifier" | "menu";

export const MenuPageHeader = ({
  children,
  crumbs,
  menuId,
}: {
  crumbs?: Crumb[];
  children?: React.ReactNode;
  menuId: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCategoryPage = location.pathname.includes("categories/");

  // V4.1: All create modals now use route-based navigation
  const openCreateMenuElementModal = (element: MenuElement) => {
    if (element === "menu") {
      void navigate({ to: "/menus/new" });
      return;
    }
    if (element === "category") {
      void navigate({ to: `/menus/${menuId}/categories/new` });
      return;
    }
    if (element === "item") {
      void navigate({ to: `/menus/${menuId}/items/new` });
      return;
    }
    if (element === "modifier") {
      void navigate({ to: `/menus/${menuId}/modifiers/new` });
      return;
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-2 pb-3"
      id="menus-header"
    >
      {crumbs ? <Breadcrumbs crumbs={crumbs} /> : <TabBar />}
      <div className="flex items-center gap-2">
        {children}
        {(!isCategoryPage || crumbs) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-1.5" id="dropdown" type="button">
                Add
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => openCreateMenuElementModal("menu")}
              >
                Menu
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openCreateMenuElementModal("category")}
              >
                Category
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openCreateMenuElementModal("item")}
              >
                Item
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openCreateMenuElementModal("modifier")}
              >
                Modifier
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

const TabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });

  return (
    <Tabs
      onValueChange={(value) => {
        void navigate({ to: `/menus/${params.menuId}/${value}` });
      }}
      value={location.pathname.split("/")[4] ?? "categories"}
    >
      <TabsList className="gap-1">
        {["categories", "items", "modifiers"].map((tab) => (
          <TabsTrigger className="capitalize" key={tab} value={tab}>
            {tab}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
